import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

const OTP_MODES = [
  { v: "off", label: "Off", desc: "Password only. No second step." },
  { v: "email", label: "Email OTP (2FA)", desc: "Password, then a 6-digit code by email." },
  { v: "sms", label: "SMS OTP (2FA)", desc: "Password, then a code by SMS. Needs Verify profile + user phone." },
  { v: "passwordless", label: "Passwordless", desc: "Sign in with just an email code — no password." },
];

function TelnyxSpendCap() {
  const qc = useQueryClient();
  const { data, isError, isLoading } = useQuery({ queryKey: ["telnyx-spend-cap"], queryFn: async () => (await api.get("/admin/telnyx/spend-cap")).data, retry: false });
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (data?.daily_spend_limit != null) setVal(String(data.daily_spend_limit)); }, [data]);
  const save = async (enabledFlag) => {
    const amount = Number(val || 0);
    setBusy(true);
    try {
      await api.put("/admin/telnyx/spend-cap", { daily_spend_limit: amount, enabled: enabledFlag });
      toast.success("Spend cap updated");
      qc.invalidateQueries({ queryKey: ["telnyx-spend-cap"] });
    } catch (e) { toast.error("Could not update spend cap"); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="spend-cap-section">
      <h2 className="font-display text-lg font-semibold">Outbound call spend cap</h2>
      <p className="mt-1 text-sm text-muted-foreground">Daily maximum your account can spend on outbound calls. Raise it for high-volume days so calls aren't cut off mid-day.</p>
      {isLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      ) : isError ? (
        <p className="mt-3 text-sm text-amber-600">Live calling isn't configured, so there's no spend cap to manage yet.</p>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-muted-foreground">$</span>
            <input data-testid="spend-cap-input" type="number" min="0" step="10" value={val} onChange={(e) => setVal(e.target.value)}
              className="w-32 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            <span className="text-sm text-muted-foreground">/ day</span>
          </div>
          <div className="mt-3 flex gap-2">
            <button data-testid="spend-cap-save" disabled={busy} onClick={() => save(true)}
              className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Save cap</button>
            <button data-testid="spend-cap-disable" disabled={busy} onClick={() => save(false)}
              className="rounded-full border border-white/10 px-5 py-2 text-sm">{data?.enabled ? "Disable cap (unlimited)" : "Cap disabled"}</button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Current: {data ? `$${data.daily_spend_limit}/day · ${data.enabled ? "enabled" : "disabled"}` : "—"}</p>
        </>
      )}
    </section>
  );
}

function BusinessHoursCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/telephony/settings").then(({ data }) => setSt(data.settings)); }, []);
  if (!st) return null;
  const upd = (k, v) => setSt((p) => ({ ...p, [k]: v }));
  const days = st.bh_days || [0, 1, 2, 3, 4, 5];
  const toggleDay = (d) => { const has = days.includes(d); upd("bh_days", has ? days.filter((x) => x !== d) : [...days, d].sort()); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/telephony/settings", {
        bh_enabled: !!st.bh_enabled, bh_timezone: st.bh_timezone || "America/New_York",
        bh_days: st.bh_days || [0, 1, 2, 3, 4, 5], bh_open: st.bh_open || "08:00", bh_close: st.bh_close || "20:00",
        after_hours_greeting: st.after_hours_greeting || "", ai_receptionist_enabled: !!st.ai_receptionist_enabled,
      });
      toast.success("Business hours saved");
    } catch { toast.error("Could not save business hours"); } finally { setBusy(false); }
  };
  const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
  const TZS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Africa/Douala", "Europe/London", "UTC"];
  return (
    <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="business-hours-section">
      <h2 className="font-display text-lg font-semibold">Business hours & after-hours</h2>
      <p className="mt-1 text-sm text-muted-foreground">Set when your line is open. Outside these hours, callers hear the after-hours greeting, then the SI receptionist (if on) or voicemail. Applies to your main iYeneh number.</p>
      <label className="mt-3 flex items-center gap-2 text-sm"><input data-testid="bh-enabled" type="checkbox" checked={!!st.bh_enabled} onChange={(e) => upd("bh_enabled", e.target.checked)} className="h-4 w-4" /> Enforce business hours</label>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div><p className="text-xs font-medium text-muted-foreground">Timezone</p>
          <select data-testid="bh-timezone" value={st.bh_timezone || "America/New_York"} onChange={(e) => upd("bh_timezone", e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-background px-2 py-2 text-sm">
            {TZS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><p className="text-xs font-medium text-muted-foreground">Open</p>
          <input data-testid="bh-open" type="time" value={st.bh_open || "08:00"} onChange={(e) => upd("bh_open", e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-background px-2 py-2 text-sm" /></div>
        <div><p className="text-xs font-medium text-muted-foreground">Close</p>
          <input data-testid="bh-close" type="time" value={st.bh_close || "20:00"} onChange={(e) => upd("bh_close", e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-background px-2 py-2 text-sm" /></div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {DAYS.map(([label, d]) => (
          <button key={d} data-testid={`bh-day-${d}`} onClick={() => toggleDay(d)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${days.includes(d) ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{label}</button>
        ))}
      </div>
      <label className="mt-4 block text-xs font-medium text-muted-foreground">After-hours greeting</label>
      <textarea data-testid="bh-greeting" rows={3} value={st.after_hours_greeting || ""} onChange={(e) => upd("after_hours_greeting", e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
      <label className="mt-3 flex items-center gap-2 text-sm"><input data-testid="bh-ai" type="checkbox" checked={!!st.ai_receptionist_enabled} onChange={(e) => upd("ai_receptionist_enabled", e.target.checked)} className="h-4 w-4" /> Use SI receptionist after hours (falls back to voicemail)</label>
      <button data-testid="bh-save" onClick={save} disabled={busy} className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Saving…" : "Save business hours"}</button>
    </section>
  );
}

export default function AdminSettings() {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { data } = useQuery({ queryKey: ["platform-settings"], queryFn: async () => (await api.get("/settings/platform")).data });
  const s = data?.settings;

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put("/settings/platform", patch);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["platform-settings"] });
      qc.invalidateQueries({ queryKey: ["public-settings"] });
    } catch (e) { toast.error("Could not save settings"); } finally { setSaving(false); }
  };

  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-settings">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Platform Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Activate the security and meeting features you want. Changes apply immediately.</p>
      </div>

      {/* MAINTENANCE MODE */}
      <section className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5" data-testid="maintenance-section">
        <h2 className="font-display text-lg font-semibold">🛠️ Maintenance mode</h2>
        <p className="mt-1 text-sm text-muted-foreground">When on, a banner appears at the top of the whole public site (home page included). Use it during upgrades or when the phone line is being worked on.</p>
        <label className="mt-4 flex items-center gap-3">
          <input type="checkbox" data-testid="maintenance-enabled" checked={!!s.maintenance_enabled}
            onChange={(e) => save({ maintenance_enabled: e.target.checked })} className="h-4 w-4" />
          <span className="text-sm font-medium">Show the maintenance banner on the site</span>
        </label>
        <div className="mt-4">
          <p className="text-sm font-medium">Message shown to visitors</p>
          <textarea data-testid="maintenance-message" rows={2} maxLength={280} defaultValue={s.maintenance_message || ""}
            placeholder="e.g. Our SI phone line is being upgraded — back shortly. Thanks for your patience!"
            onBlur={(e) => e.target.value !== (s.maintenance_message || "") && save({ maintenance_message: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          <p className="mt-1 text-xs text-muted-foreground">Leave blank to use the default message. Saved when you click away.</p>
        </div>
      </section>

      {/* OTP */}
      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Sign-in security (OTP)</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose how people sign in to iyeneh.com.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {OTP_MODES.map((m) => (
            <button key={m.v} data-testid={`otp-mode-${m.v}`} disabled={saving} onClick={() => save({ otp_mode: m.v })}
              className={`rounded-xl border p-4 text-left transition-colors ${s.otp_mode === m.v ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">{m.label}</span>
                {s.otp_mode === m.v && <CheckCircle size={18} weight="fill" className="text-primary" />}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{m.desc}</p>
            </button>
          ))}
        </div>
        <div className="mt-4">
          <p className="text-sm font-medium">Who does OTP apply to?</p>
          <div className="mt-2 flex gap-2">
            {[["all", "Everyone"], ["staff", "Admins only"]].map(([v, l]) => (
              <button key={v} data-testid={`otp-scope-${v}`} disabled={saving} onClick={() => save({ otp_scope: v })}
                className={`rounded-full px-4 py-2 text-sm ${s.otp_scope === v ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
        </div>
        <label className="mt-4 flex items-start gap-3 rounded-xl border border-white/10 p-3">
          <input type="checkbox" data-testid="otp-exempt-owner" checked={s.otp_exempt_owner}
            onChange={(e) => save({ otp_exempt_owner: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">Break-glass: always let the owner sign in with just a password.
            <span className="mt-0.5 block text-xs text-muted-foreground">Keep this on so a mail/SMS outage can never lock you out. Turn off only for maximum security.</span></span>
        </label>
        <div className="mt-4">
          <p className="text-sm font-medium">SMS OTP — Telnyx Verify Profile ID</p>
          <p className="text-xs text-muted-foreground">Paste your Verify profile ID here to activate SMS codes (no redeploy needed). Leave blank to use email codes.</p>
          <input data-testid="verify-profile-id" defaultValue={s.telnyx_verify_profile_id} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            onBlur={(e) => e.target.value !== s.telnyx_verify_profile_id && save({ telnyx_verify_profile_id: e.target.value.trim() })}
            className="mt-2 w-full max-w-md rounded-xl border border-white/10 bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
        </div>
      </section>

      {/* Video */}
      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Video meetings engine</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {[["webrtc", "Built-in WebRTC", "Peer-to-peer. Best for up to ~6 people. No extra cost. Active."],
            ["telnyx", "Large-room (SFU)", "Scales to big webinars + cloud recording. Beta — needs provider provisioning."]].map(([v, l, d]) => (
            <button key={v} data-testid={`video-mode-${v}`} disabled={saving} onClick={() => save({ video_mode: v })}
              className={`rounded-xl border p-4 text-left transition-colors ${s.video_mode === v ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}>
              <div className="flex items-center justify-between"><span className="font-semibold">{l}</span>
                {s.video_mode === v && <CheckCircle size={18} weight="fill" className="text-primary" />}</div>
              <p className="mt-1 text-xs text-muted-foreground">{d}</p>
            </button>
          ))}
        </div>
        <div className="mt-4 space-y-3">
          <label className="flex items-start gap-3 rounded-xl border border-white/10 p-3">
            <input type="checkbox" data-testid="waiting-room" checked={s.waiting_room}
              onChange={(e) => save({ waiting_room: e.target.checked })} className="mt-0.5 h-4 w-4" />
            <span className="text-sm">Meeting lock & waiting room
              <span className="mt-0.5 block text-xs text-muted-foreground">Guests wait until the host admits them — no uninvited people.</span></span>
          </label>
          <label className="flex items-start gap-3 rounded-xl border border-white/10 p-3">
            <input type="checkbox" data-testid="live-captions" checked={s.live_captions}
              onChange={(e) => save({ live_captions: e.target.checked })} className="mt-0.5 h-4 w-4" />
            <span className="text-sm">Live captions
              <span className="mt-0.5 block text-xs text-muted-foreground">Real-time on-screen captions during calls (best in Chrome). Recordings still get SI transcripts.</span></span>
          </label>
        </div>
      </section>

      {/* Referral rewards */}
      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Refer &amp; Earn rewards</h2>
        <p className="mt-1 text-sm text-muted-foreground">Give customers wallet credit for inviting friends — grows your base with no ad spend.</p>
        <label className="mt-3 flex items-start gap-3 rounded-xl border border-white/10 p-3">
          <input type="checkbox" data-testid="referral-enabled" checked={s.referral_enabled}
            onChange={(e) => save({ referral_enabled: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">Enable Refer &amp; Earn
            <span className="mt-0.5 block text-xs text-muted-foreground">Both the referrer and the new user get the bonus below as promo credit.</span></span>
        </label>
        <div className="mt-3">
          <p className="text-sm font-medium">Bonus per successful referral (USD)</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-muted-foreground">$</span>
            <input data-testid="referral-bonus" type="number" min="0" step="0.5" defaultValue={(s.referral_bonus_cents / 100).toString()}
              onBlur={(e) => { const c = Math.round(Number(e.target.value) * 100); if (c !== s.referral_bonus_cents) save({ referral_bonus_cents: c }); }}
              className="w-28 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
        </div>
        <label className="mt-4 flex items-start gap-3 rounded-xl border border-white/10 p-3">
          <input type="checkbox" data-testid="autotopup-email" checked={s.autotopup_email_enabled}
            onChange={(e) => save({ autotopup_email_enabled: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">Monthly "set it &amp; forget it" email
            <span className="mt-0.5 block text-xs text-muted-foreground">Automatically emails customers who bought once but haven't set up a recurring top-up, inviting them to automate — recurring margin on autopilot.</span></span>
        </label>
        <label className="mt-3 flex items-start gap-3 rounded-xl border border-white/10 p-3">
          <input type="checkbox" data-testid="winback-enabled" checked={s.winback_enabled}
            onChange={(e) => save({ winback_enabled: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">Win-back "we miss you" email
            <span className="mt-0.5 block text-xs text-muted-foreground">Weekly email to customers with no top-up in 30+ days, with an optional welcome-back bonus below.</span></span>
        </label>
        <div className="mt-3">
          <p className="text-sm font-medium">Win-back welcome bonus (USD, 0 = email only)</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-muted-foreground">$</span>
            <input data-testid="winback-bonus" type="number" min="0" step="0.5" defaultValue={(s.winback_bonus_cents / 100).toString()}
              onBlur={(e) => { const cts = Math.round(Number(e.target.value) * 100); if (cts !== s.winback_bonus_cents) save({ winback_bonus_cents: cts }); }}
              className="w-28 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
        </div>
      </section>

      {/* Wallet transfer fees */}
      <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="transfer-fee-section">
        <h2 className="font-display text-lg font-semibold">Member-to-member transfer fee</h2>
        <p className="mt-1 text-sm text-muted-foreground">What you charge when a member sends wallet money to another member. Fee = percentage of the amount <strong>plus</strong> the flat fee. Staff (owner/admin) are always exempt.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm font-medium">Percentage fee (%)</p>
            <div className="mt-2 flex items-center gap-2">
              <input data-testid="transfer-fee-pct" type="number" min="0" max="100" step="0.5" defaultValue={(s.transfer_fee_pct ?? 0).toString()}
                onBlur={(e) => { const v = Math.max(0, Math.min(100, Number(e.target.value) || 0)); if (v !== s.transfer_fee_pct) save({ transfer_fee_pct: v }); }}
                className="w-28 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              <span className="text-sm text-muted-foreground">% of amount</span>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium">Flat fee (USD)</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-muted-foreground">$</span>
              <input data-testid="transfer-fee-flat" type="number" min="0" step="0.05" defaultValue={((s.transfer_fee_flat_cents ?? 0) / 100).toString()}
                onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== s.transfer_fee_flat_cents) save({ transfer_fee_flat_cents: c }); }}
                className="w-28 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              <span className="text-sm text-muted-foreground">per transfer</span>
            </div>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Example on a $10.00 transfer: fee = ${(((s.transfer_fee_pct ?? 0) * 10 / 100) + ((s.transfer_fee_flat_cents ?? 0) / 100)).toFixed(2)}.</p>
      </section>

      {/* Dial-in */}
      <section className="rounded-2xl border border-white/10 bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Meeting dial-in</h2>
        <p className="mt-1 text-sm text-muted-foreground">Show a phone number guests can call to join by voice.</p>
        <label className="mt-3 flex items-center gap-3">
          <input type="checkbox" data-testid="dialin-enabled" checked={s.dialin_enabled}
            onChange={(e) => save({ dialin_enabled: e.target.checked })} className="h-4 w-4" />
          <span className="text-sm">Enable dial-in display</span>
        </label>
        <input data-testid="dialin-number" defaultValue={s.dialin_number} placeholder="+1 202 970 8845"
          onBlur={(e) => e.target.value !== s.dialin_number && save({ dialin_number: e.target.value })}
          className="mt-3 w-full max-w-xs rounded-xl border border-white/10 bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
      </section>

      <TelnyxSpendCap />

      {/* Internet calls */}
      <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="internet-call-section">
        <h2 className="font-display text-lg font-semibold">Internet calls (member-to-member)</h2>
        <p className="mt-1 text-sm text-muted-foreground">Free WebRTC audio/video calls between members. Set a per-minute price to earn on every call (e.g. to pay your team). Leave at 0 to keep it free. You are never charged — these run over the internet, not a carrier.</p>
        <div className="mt-4">
          <p className="text-sm font-medium">Price per minute (¢)</p>
          <div className="mt-2 flex items-center gap-2">
            <input data-testid="internet-call-rate" type="number" min="0" max="1000" step="1" defaultValue={(s.internet_call_rate_cpm ?? 0).toString()}
              onBlur={(e) => { const v = Math.max(0, Math.min(1000, Math.round(Number(e.target.value)) || 0)); if (v !== s.internet_call_rate_cpm) save({ internet_call_rate_cpm: v }); }}
              className="w-28 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            <span className="text-sm text-muted-foreground">¢ / minute {(s.internet_call_rate_cpm ?? 0) === 0 ? "· currently FREE" : ""}</span>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Callers must be active members with wallet funds. A 5-minute call at {(s.internet_call_rate_cpm ?? 0)}¢/min bills the caller ${(((s.internet_call_rate_cpm ?? 0) * 5) / 100).toFixed(2)}.</p>
        <label className="mt-4 flex items-start gap-2 text-sm" data-testid="call-sms-fallback">
          <input type="checkbox" checked={!!s.call_sms_fallback}
            onChange={(e) => save({ call_sms_fallback: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span>Ring their phone even when offline — if a member can't be reached by app notification, send them an SMS ("📞 X is calling you… tap to answer"). Uses your SMS credit per call.</span>
        </label>
      </section>

      {/* Buy Credit — manual top-up config */}
      <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="manual-topup-section">
        <h2 className="font-display text-lg font-semibold">Buy Credit — manual top-up</h2>
        <p className="mt-1 text-sm text-muted-foreground">Shown on your public landing page. Diaspora pay by Zelle/CashApp, Africa by Mobile Money, then forward the receipt to your WhatsApp to be credited. Leave blank to hide a method.</p>
        <label className="mt-3 flex items-center gap-3">
          <input type="checkbox" data-testid="manual-topup-enabled" checked={!!s.manual_topup_enabled} onChange={(e) => save({ manual_topup_enabled: e.target.checked })} className="h-4 w-4" />
          <span className="text-sm">Show the Buy Credit section publicly</span>
        </label>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {[
            { k: "pay_zelle", label: "Zelle (email or phone)", ph: "you@email.com" },
            { k: "pay_cashapp", label: "CashApp $cashtag", ph: "$iYenehPay" },
            { k: "pay_mtn_momo", label: "MTN Mobile Money number", ph: "+237 6XX XXX XXX" },
            { k: "pay_mtn_momo_name", label: "MTN MoMo registered name", ph: "iYeneh Connect" },
            { k: "pay_orange_momo", label: "Orange Money number", ph: "+237 6XX XXX XXX" },
            { k: "pay_orange_momo_name", label: "Orange Money registered name", ph: "iYeneh Connect" },
          ].map((f) => (
            <div key={f.k}>
              <p className="text-sm font-medium">{f.label}</p>
              <input data-testid={`cfg-${f.k}`} defaultValue={s[f.k] || ""} placeholder={f.ph}
                onBlur={(e) => e.target.value !== (s[f.k] || "") && save({ [f.k]: e.target.value })}
                className="mt-1.5 w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
          ))}
          <div className="sm:col-span-2">
            <p className="text-sm font-medium">WhatsApp receipt number(s) — comma separated, with country code</p>
            <input data-testid="cfg-topup_whatsapp_numbers" defaultValue={s.topup_whatsapp_numbers || ""} placeholder="+237600000000, +14155550100"
              onBlur={(e) => e.target.value !== (s.topup_whatsapp_numbers || "") && save({ topup_whatsapp_numbers: e.target.value })}
              className="mt-1.5 w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">Display rate (FCFA per $1)</p>
            <input data-testid="cfg-xaf_per_usd" type="number" min="1" step="1" defaultValue={(s.xaf_per_usd ?? 600).toString()}
              onBlur={(e) => { const v = Math.max(1, Math.round(Number(e.target.value)) || 600); if (v !== s.xaf_per_usd) save({ xaf_per_usd: v }); }}
              className="mt-1.5 w-40 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
        </div>
        <div className="mt-4 border-t border-white/10 pt-4">
          <label className="flex items-center gap-3">
            <input type="checkbox" data-testid="weekend-bonus-enabled" checked={!!s.weekend_bonus_enabled} onChange={(e) => save({ weekend_bonus_enabled: e.target.checked })} className="h-4 w-4" />
            <span className="text-sm font-medium">⚡ Weekend Bonus — auto-boost the first top-up bonus Fri–Sun (shows a live countdown on your homepage)</span>
          </label>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Boost</span>
            <input data-testid="weekend-bonus-pct" type="number" min="0" max="100" step="5" defaultValue={(s.weekend_bonus_pct ?? 20).toString()}
              onBlur={(e) => { const v = Math.max(0, Math.min(100, Math.round(Number(e.target.value)) || 0)); if (v !== s.weekend_bonus_pct) save({ weekend_bonus_pct: v }); }}
              className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            <span className="text-sm text-muted-foreground">% extra on the first-top-up bonus</span>
          </div>
        </div>
      </section>

      {/* Business PBX (Auto-Attendant) tier */}
      <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="business-pbx-section">
        <h2 className="font-display text-lg font-semibold">Business PBX (Auto-Attendant)</h2>
        <p className="mt-1 text-sm text-muted-foreground">A paid tier that turns a customer's number into a live IVR menu ("press 0 for service, 1 for support…"). Grant it to a customer directly, or let them self-subscribe from their Call Settings.</p>
        <div className="mt-4">
          <p className="text-sm font-medium">Monthly price (USD)</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-muted-foreground">$</span>
            <input data-testid="pbx-price" type="number" min="0" step="0.5" defaultValue={(((s.pbx_business_price_cents ?? 0)) / 100).toString()}
              onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== s.pbx_business_price_cents) save({ pbx_business_price_cents: c }); }}
              className="w-28 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            <span className="text-sm text-muted-foreground">per customer / month (0 = free)</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[["pbx_quarterly_price_cents", "Quarterly (3 mo)", 2799], ["pbx_semi_price_cents", "Semi-annual (6 mo)", 5299], ["pbx_annual_price_cents", "Annual (12 mo)", 9999]].map(([k, label, def]) => (
              <div key={k}>
                <p className="text-xs font-medium text-muted-foreground">{label}</p>
                <div className="mt-1 flex items-center gap-1"><span className="text-muted-foreground">$</span>
                  <input data-testid={`price-${k}`} type="number" min="0" step="0.5" defaultValue={(((s[k] ?? def)) / 100).toString()}
                    onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== (s[k] ?? def)) save({ [k]: c }); }}
                    className="w-24 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <BusinessPbxManager />
      </section>

      {/* iYeneh HD Meetings plans */}
      <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="hd-meetings-section">
        <h2 className="font-display text-lg font-semibold">iYeneh HD Meetings — plans</h2>
        <p className="mt-1 text-sm text-muted-foreground">Hosting an HD meeting requires a plan (joining stays free). Charged from the member's wallet. Set each billing cycle's price.</p>
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pro (individuals & small teams)</p>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["meet_pro_monthly_cents", "Monthly", 999], ["meet_pro_quarterly_cents", "Quarterly", 2199], ["meet_pro_semi_cents", "Semi-annual", 3999], ["meet_pro_annual_cents", "Annual", 6999]].map(([k, label, def]) => (
            <div key={k}><p className="text-xs font-medium text-muted-foreground">{label}</p>
              <div className="mt-1 flex items-center gap-1"><span className="text-muted-foreground">$</span>
                <input data-testid={`price-${k}`} type="number" min="0" step="0.5" defaultValue={(((s[k] ?? def)) / 100).toString()}
                  onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== (s[k] ?? def)) save({ [k]: c }); }}
                  className="w-full rounded-xl border border-white/10 bg-background px-2 py-2 text-sm outline-none focus:border-primary" /></div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Business (multi-host, advanced)</p>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["meet_biz_monthly_cents", "Monthly", 1499], ["meet_biz_annual_cents", "Annual", 12999]].map(([k, label, def]) => (
            <div key={k}><p className="text-xs font-medium text-muted-foreground">{label}</p>
              <div className="mt-1 flex items-center gap-1"><span className="text-muted-foreground">$</span>
                <input data-testid={`price-${k}`} type="number" min="0" step="0.5" defaultValue={(((s[k] ?? def)) / 100).toString()}
                  onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== (s[k] ?? def)) save({ [k]: c }); }}
                  className="w-full rounded-xl border border-white/10 bg-background px-2 py-2 text-sm outline-none focus:border-primary" /></div>
            </div>
          ))}
        </div>
      </section>

      {/* iYeneh Fleet — per-vehicle tracking packages */}
      <section className="rounded-2xl border border-white/10 bg-card p-5" data-testid="fleet-billing-section">
        <h2 className="font-display text-lg font-semibold">iYeneh Fleet tracking packages</h2>
        <p className="mt-1 text-sm text-muted-foreground">Companies pay a monthly fee <b>per vehicle</b>, discounted by fleet size. Each vehicle gets a free trial before billing starts; tracking pauses (history kept) if the wallet can't cover a renewal.</p>
        <label className="mt-3 flex items-start gap-3 text-sm">
          <input data-testid="fleet-billing-enabled" type="checkbox" checked={!!s.fleet_billing_enabled}
            onChange={(e) => save({ fleet_billing_enabled: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span>Fleet billing enabled <span className="text-muted-foreground">(off = every vehicle is free)</span></span>
        </label>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <div>
            <p className="text-sm font-medium">Free trial (days)</p>
            <input data-testid="fleet-trial-days" type="number" min="0" step="1" defaultValue={(s.fleet_trial_days ?? 14).toString()}
              onBlur={(e) => { const v = Math.max(0, Math.round(Number(e.target.value)) || 0); if (v !== s.fleet_trial_days) save({ fleet_trial_days: v }); }}
              className="mt-2 w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          {[["Bronze (1)", "fleet_price_bronze_cents"], ["Silver (2–4)", "fleet_price_silver_cents"], ["Gold (5–9)", "fleet_price_gold_cents"], ["Platinum (10+)", "fleet_price_platinum_cents"]].map(([label, key]) => (
            <div key={key}>
              <p className="text-sm font-medium">{label}</p>
              <div className="mt-2 flex items-center gap-1">
                <span className="text-muted-foreground">$</span>
                <input data-testid={`fleet-price-${key}`} type="number" min="0" step="0.1" defaultValue={((s[key] ?? 0) / 100).toString()}
                  onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== s[key]) save({ [key]: c }); }}
                  className="w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">≈ {Math.round((s[key] ?? 0) / 100 * (s.xaf_per_usd ?? 600)).toLocaleString()} XAF /mo</p>
            </div>
          ))}
        </div>
        <div className="mt-4 max-w-xs">
          <p className="text-sm font-medium">GPS box add-on (per vehicle / mo)</p>
          <div className="mt-2 flex items-center gap-1">
            <span className="text-muted-foreground">$</span>
            <input data-testid="fleet-device-addon" type="number" min="0" step="0.1" defaultValue={((s.fleet_device_addon_cents ?? 0) / 100).toString()}
              onBlur={(e) => { const c = Math.max(0, Math.round(Number(e.target.value) * 100) || 0); if (c !== s.fleet_device_addon_cents) save({ fleet_device_addon_cents: c }); }}
              className="w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Extra monthly fee when a company installs an in-vehicle GPS tracker (device) instead of the free phone tracker. ≈ {Math.round((s.fleet_device_addon_cents ?? 0) / 100 * (s.xaf_per_usd ?? 600)).toLocaleString()} XAF /mo</p>
        </div>
      </section>

      {saving && <p className="flex items-center gap-2 text-sm text-muted-foreground"><ArrowsClockwise size={14} className="animate-spin" /> Saving…</p>}
    </div>
  );
}

function BusinessPbxManager() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["admin-business-pbx"], queryFn: async () => (await api.get("/admin/business-pbx")).data.customers });
  const act = async (targetEmail, enabled) => {
    if (!targetEmail.trim()) { toast.error("Enter a customer email"); return; }
    setBusy(true);
    try {
      await api.post("/admin/business-pbx", { email: targetEmail.trim(), enabled });
      toast.success(enabled ? "Business PBX granted" : "Business PBX revoked");
      setEmail("");
      qc.invalidateQueries({ queryKey: ["admin-business-pbx"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 border-t border-white/10 pt-4">
      <p className="text-sm font-medium">Grant / revoke by customer</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <input data-testid="pbx-grant-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@email.com"
          className="min-w-[220px] flex-1 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <button data-testid="pbx-grant" onClick={() => act(email, true)} disabled={busy} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Grant</button>
        <button data-testid="pbx-revoke" onClick={() => act(email, false)} disabled={busy} className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold transition-colors hover:text-destructive disabled:opacity-60">Revoke</button>
      </div>
      <div className="mt-4 space-y-1" data-testid="pbx-customer-list">
        {(data || []).length === 0 && <p className="text-sm text-muted-foreground">No Business PBX customers yet.</p>}
        {(data || []).map((c) => (
          <div key={c.user_id} className="flex items-center justify-between rounded-lg bg-background px-3 py-2 text-sm">
            <div><span className="font-medium">{c.email}</span> <span className="text-xs text-muted-foreground">{c.owner_grant ? "· granted" : ""}{c.subscription ? "· subscribed" : ""} {c.ivr_enabled ? "· IVR live" : "· IVR off"}</span></div>
            <button data-testid={`pbx-revoke-${c.user_id}`} onClick={() => act(c.email, false)} className="text-xs font-semibold text-muted-foreground transition-colors hover:text-destructive">Revoke</button>
          </div>
        ))}
      </div>
    </div>
  );
}
